/**
 * GraphUI
 *
 * Card list for editing graph relationships (entity + record pairs) on a field.
 * Consumers: the `graph` field renderer inside a form or entity record body.
 *
 * The only form-store leaf it reads is `splitQuery`, off a narrow snapshot, to
 * hide relationships outside the active split. `useFormStore()` is banned here:
 * this component sits in the field render tree, and that hook's spread of
 * `formVersion.data` re-rendered every relationship card on each keystroke
 * anywhere in the form (SYD-2252).
 */

import { FC, useEffect, useMemo } from 'react';
import {
    Button,
    Input,
    Select,
    Popconfirm,
    Tooltip,
    Typography,
    theme
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import FlexBox from '../../atoms/FlexBox';
import FlexCol from '../../atoms/FlexCol';
import FlexCard from '../../atoms/FlexCard';
import { MAX_LIMITED_WIDTH_VIEWPORT } from '#types/System.Parameters.types';
import type { Entity } from '#types/System.types';
import { createCollectionStore, formStore } from '../../../store';
import EntityRecordUI from '../../atoms/EntityRecordUI';

const entitiesStore = createCollectionStore<Entity>('entities', {
    queryOptions: { extraConstraints: [{ type: 'limit', n: 50 }] },
});

const { Text } = Typography;

export interface Relationship {
    /** The ID of the entity */
    entityId: string;
    /** Optional entity group ID to filter records */
    entityGroupId?: string;
    /** The ID of the entity record */
    recordId: string;
    /** Optional notes about this relationship */
    notes: string;
    /** Optional path in Firestore/DB to the target doc. */
    path?: string;
    /** Any extra metadata you want to store. */
    meta?: Record<string, any>;
    /** Optional child relationships for one-to-many support */
    children?: Relationship[];
    /** Optional record object */
    record?: any;
}

interface GraphUIProps {
    /** Current relationships data */
    value: Relationship[];
    /** Callback when relationships change */
    onChange: (newRelationships: Relationship[]) => void;
    /** Whether the control is disabled */
    disabled?: boolean;
    /** Additional style properties */
    style?: React.CSSProperties;
    /** Optional filter for allowed entity types */
    allowedEntities?: string[];
    /** Optional depth for recursive GraphUI, default is 1 */
    depth?: number;
}

/**
 * Renders a list of cards for managing graph relationships between documents
 */
const GraphUI: FC<GraphUIProps> = ({
    value = [],
    onChange,
    disabled = false,
    style,
    allowedEntities,
    depth = 1
}) => {
    const splitQuery = useSnapshot(formStore.state).splitQuery;

    // Apply split filtering by hiding relationships rather than filtering data
    const visibleRelationships = useMemo(() => {
        if (!splitQuery || !value) {
            return value;
        }

        return value.filter(relationship => {
            if (relationship.entityId === splitQuery.queryValue) {
                return true;
            }
            if (relationship.recordId === splitQuery.queryValue) {
                return true;
            }
            if (relationship.record && relationship.record.name === splitQuery.queryValue) {
                return true;
            }
            return false;
        });
    }, [value, splitQuery]);
    const { token } = theme.useToken();
    useEffect(() => {
        entitiesStore.connect();
        return () => entitiesStore.disconnect();
    }, []);
    const entitiesSnap = useSnapshot(entitiesStore.state);
    const entities = useMemo<Entity[]>(() => {
        const result: Entity[] = [];
        for (const id of entitiesSnap.docIds) {
            const entry = entitiesSnap.docs.get(id);
            if (entry) result.push({ ...(entry.data as Entity), docId: id });
        }
        return result;
    }, [entitiesSnap.docIds, entitiesSnap.docs]);

    const filteredEntities = allowedEntities && allowedEntities.length
        ? entities.filter((entity: any) => allowedEntities.includes(entity.docId))
        : entities;

    const createNewRelationship = (): Relationship => ({
        entityId: '',
        recordId: '',
        notes: '',
        meta: {}
    });

    const handleRelationshipChange = (index: number, field: keyof Relationship, newValue: string) => {
        const newRelationships = [...value];
        newRelationships[index] = { ...newRelationships[index], [field]: newValue };
        onChange(newRelationships);
    };

    const handleDelete = (index: number) => {
        const newRelationships = [...value];
        newRelationships.splice(index, 1);
        onChange(newRelationships);
    };

    const handleChildrenChange = (index: number, newChildren: Relationship[]) => {
        const newRelationships = [...value];
        newRelationships[index] = { ...newRelationships[index], children: newChildren };
        onChange(newRelationships);
    };

    const handleAddRelationship = () => {
        onChange([...value, createNewRelationship()]);
    };

    const handleAddChildRelationship = (index: number) => {
        if (depth >= 5) return;
        const updatedRelationship = {
            ...value[index],
            children: [...(value[index].children || []), createNewRelationship()]
        };
        const newRelationships = [...value];
        newRelationships[index] = updatedRelationship;
        onChange(newRelationships);
    };

    const handleEntityChange = (index: number, entityId: string, entityGroupId?: string) => {
        const newRelationships = [...value];
        newRelationships[index] = {
            ...newRelationships[index],
            entityId,
            entityGroupId,
            recordId: '',
        };
        onChange(newRelationships);
    };

    const handleEntityRecordSelect = (index: number, recordId?: string, record?: any) => {
        const newRelationships = [...value];
        newRelationships[index] = {
            ...newRelationships[index],
            recordId: recordId || '',
            record: record || null,
            notes: ''
        };
        onChange(newRelationships);
    };

    return (
        <FlexCol
            style={{
                width: '100%',
                maxWidth: MAX_LIMITED_WIDTH_VIEWPORT,
                ...style,
            }}
            gap={5}
        >
            {visibleRelationships.map((relationship, index) => (
                <FlexCard
                    key={`${relationship.entityId}-${relationship.recordId}-${index}`}
                    stretch
                    style={{
                        width: '100%',
                        backgroundColor: token.colorSplit,
                    }}
                    styles={{
                        body: {
                            paddingTop: 5,
                            paddingBottom: 5
                        },
                    }}
                >
                    <FlexBox stretch>
                        <FlexCol
                            gap={0}
                            style={{
                                flex: 1,
                                maxWidth: 200,
                            }}
                        >
                            <Text>Record</Text>
                            <Select<string>
                                variant='filled'
                                value={relationship.entityId || undefined}
                                placeholder="Select entity"
                                disabled={disabled}
                                style={{
                                    width: '100%',
                                    marginBottom: 10
                                }}
                                onChange={(entityId) => handleEntityChange(index, entityId, relationship.entityGroupId)}
                                options={filteredEntities.map((entity) => ({
                                    label: entity.description?.shortLabel || entity.docId,
                                    value: entity.docId,
                                }))}
                            />
                            <EntityRecordUI
                                entityId={relationship.entityId}
                                entityGroupId={relationship.entityGroupId}
                                enableExternalOnChange={true}
                                externalOnChange={(recordId, record) => handleEntityRecordSelect(index, recordId, record)}
                                disabled={disabled}
                                style={{ width: '100%' }}
                            />
                        </FlexCol>
                        <FlexCol gap={0} style={{ flex: 4 }}>
                            <Text>Notes</Text>
                            <Input.TextArea
                                variant='filled'
                                value={relationship.notes}
                                placeholder="Add notes"
                                style={{
                                    height: '100%',
                                    resize: 'none'
                                }}
                                disabled={disabled}
                                onChange={(e) => handleRelationshipChange(index, 'notes', e.target.value)}
                            />
                        </FlexCol>
                        <FlexCol stretch noGrow alignEnd justifyEnd id='graph-ui-actions'>
                            <Tooltip title="Delete relationship">
                                <Popconfirm
                                    title="Delete this relationship?"
                                    onConfirm={() => handleDelete(index)}
                                    disabled={disabled}
                                >
                                    <Button
                                        type='text'
                                        disabled={disabled}
                                        icon={<DeleteOutlined />}
                                    />
                                </Popconfirm>
                            </Tooltip>
                            <Tooltip title="Add child relationship">
                                <Button
                                    type='text'
                                    disabled={disabled || depth >= 5}
                                    icon={<PlusOutlined />}
                                    onClick={() => handleAddChildRelationship(index)}
                                />
                            </Tooltip>
                        </FlexCol>
                    </FlexBox>
                    <FlexCol>
                        {relationship.children && depth < 5 && (
                            <FlexCol style={{ marginLeft: 20 }}>
                                <GraphUI
                                    value={relationship.children}
                                    onChange={(childRelationships) => handleChildrenChange(index, childRelationships)}
                                    disabled={disabled}
                                    allowedEntities={allowedEntities}
                                    depth={depth + 1}
                                />
                            </FlexCol>
                        )}
                    </FlexCol>
                </FlexCard>
            ))}
            <FlexBox stretch>
                <Tooltip title="Add new relationship">
                    <Button
                        type='link'
                        disabled={disabled}
                        icon={<PlusOutlined />}
                        onClick={handleAddRelationship}
                    >
                        Add new relationship
                    </Button>
                </Tooltip>
            </FlexBox>
        </FlexCol>
    );
};

export default GraphUI;
